/**
 * StoryArcRail — "this story so far" on the breaking-news reader (Leg 6, decision C4).
 *
 * The current beat's siblings in the same story arc, chronologically, with the current
 * beat marked in place so the list reads as a timeline. Rendered from the CURRENT beat's
 * vantage ("earlier / later in this story"), which is why the arc needs no headline of its
 * own — there is no story title here, no link to a story page, and no story route to link
 * to. Stories stay admin-only as an object; only this rail is public.
 *
 * Presentational only. The host page resolves and GATES the arc (lib/server/story-arc.ts:
 * a multi-event arc resolves to null and nothing renders here). Sits in the context column
 * beside PieceContext and follows its idiom exactly.
 */

'use client';

import React from 'react';
import Link from 'next/link';
import { formatRelativeDate } from '@/utils/date-formatting';
import type { StoryArc, StoryArcItem } from '@/lib/server/story-arc';

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-scout-gold">
      {children}
    </div>
  );
}

function ArcLink({ item }: { item: StoryArcItem }) {
  return (
    <Link
      href={item.href}
      className="group block rounded px-3 py-2.5 transition-colors hover:bg-surface"
    >
      <span className="block font-body text-sm leading-snug text-parchment transition-colors group-hover:text-scout-gold">
        {item.title}
      </span>
      {item.publishedAt && (
        <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.12em] text-parchment-dim">
          {formatRelativeDate(item.publishedAt)}
        </span>
      )}
    </Link>
  );
}

export default function StoryArcRail({ arc }: { arc: StoryArc | null | undefined }) {
  if (!arc) return null;
  const { earlier = [], later = [] } = arc;
  if (!earlier.length && !later.length) return null;

  return (
    <section>
      <SectionHeading>This story so far</SectionHeading>
      <div className="space-y-1">
        {earlier.map((item) => (
          <ArcLink key={item.href} item={item} />
        ))}

        {/* YOU ARE HERE — the pivot that makes "earlier / later" legible without giving the
            arc a headline. Not a link: it is the page you are on. */}
        <div className="flex items-center gap-2 px-3 py-2.5">
          <span className="h-1 w-1 flex-none rounded-full bg-scout-gold" aria-hidden="true" />
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-parchment-dim">
            You are here
          </span>
        </div>

        {later.map((item) => (
          <ArcLink key={item.href} item={item} />
        ))}
      </div>
    </section>
  );
}
